/**
 * #186 tracked time: the API seam. Production uses the generated SDK; tests install an in-memory source
 * (src/test/feature-fixture-defaults.ts) so the fixtures never reach the production bundle.
 */
import { queryOptions } from '@tanstack/react-query'
import { unwrap } from '../../lib/api/client'
import {
  logTimeApiV1TimePost,
  type TimeEntryIn,
  type TimeEntryOut,
  type TrackedTimeOut,
  trackedApiV1CasesCaseIdTrackedTimeGet,
  trackedDiscardApiV1CasesCaseIdTrackedTimeDiscardPost,
  trackedHeartbeatApiV1CasesCaseIdTrackedTimeHeartbeatPost,
  type UnloggedTimeRow,
  unloggedApiV1TimeUnloggedGet,
} from '../../lib/api/generated'

export type { TrackedTimeOut, UnloggedTimeRow }

export interface TrackedApi {
  get(caseUuid: string, signal?: AbortSignal): Promise<TrackedTimeOut>
  heartbeat(caseUuid: string, seconds: number): Promise<TrackedTimeOut>
  discard(caseUuid: string): Promise<TrackedTimeOut>
  unlogged(signal?: AbortSignal): Promise<UnloggedTimeRow[]>
  /** POST /time (the daily card logs the accumulated time as one entry). */
  log(body: TimeEntryIn): Promise<TimeEntryOut>
}

const httpTrackedApi: TrackedApi = {
  get: (caseUuid, signal) => unwrap(trackedApiV1CasesCaseIdTrackedTimeGet({ path: { case_id: caseUuid }, signal })),
  heartbeat: (caseUuid, seconds) =>
    unwrap(
      trackedHeartbeatApiV1CasesCaseIdTrackedTimeHeartbeatPost({
        path: { case_id: caseUuid },
        body: { seconds },
        // A flush on page hide must outlive the page.
        keepalive: true,
      }),
    ),
  discard: (caseUuid) => unwrap(trackedDiscardApiV1CasesCaseIdTrackedTimeDiscardPost({ path: { case_id: caseUuid } })),
  unlogged: (signal) => unwrap(unloggedApiV1TimeUnloggedGet({ signal })),
  log: (body) => unwrap(logTimeApiV1TimePost({ body })),
}

let current: TrackedApi = httpTrackedApi

export const trackedApi = (): TrackedApi => current

/** Tests only (src/test/feature-fixture-defaults.ts). */
export function installTrackedTestDefault(api: TrackedApi): void {
  current = api
}

export const trackedKeys = {
  case: (caseUuid: string) => ['tracked-time', caseUuid] as const,
  unlogged: ['tracked-time', 'unlogged'] as const,
}

export const trackedQuery = (caseUuid: string) =>
  queryOptions({
    queryKey: trackedKeys.case(caseUuid),
    queryFn: ({ signal }) => current.get(caseUuid, signal),
    staleTime: 15_000,
    retry: false,
    enabled: Boolean(caseUuid),
  })

export const unloggedQuery = () =>
  queryOptions({ queryKey: trackedKeys.unlogged, queryFn: ({ signal }) => current.unlogged(signal), staleTime: 60_000, retry: false })

/** An in-memory TrackedApi (tests; mirrors the server's rules closely enough for the screens). */
export function createMemoryTrackedApi(seed: Record<string, Partial<TrackedTimeOut>> = {}): TrackedApi & {
  beats: { caseUuid: string; seconds: number }[]
  logged: TimeEntryIn[]
} {
  const logged: TimeEntryIn[] = []
  const rows = new Map<string, TrackedTimeOut>()
  const beats: { caseUuid: string; seconds: number }[] = []
  const threshold = 15
  const row = (caseUuid: string): TrackedTimeOut =>
    rows.get(caseUuid) ?? {
      case_id: caseUuid,
      seconds: 0,
      minutes: 0,
      replies: 0,
      source: 'tracked: 0 min',
      threshold_minutes: threshold,
      prompt_due: false,
      ...seed[caseUuid],
    }
  const put = (caseUuid: string, seconds: number, replies: number, promptDue = false) => {
    const out: TrackedTimeOut = {
      ...row(caseUuid),
      seconds,
      minutes: seconds > 0 ? Math.max(1, Math.round(seconds / 60)) : 0,
      replies,
      source: trackedSourceText(seconds, replies),
      prompt_due: promptDue,
    }
    rows.set(caseUuid, out)
    return out
  }
  for (const [caseUuid, r] of Object.entries(seed)) put(caseUuid, r.seconds ?? 0, r.replies ?? 0)
  return {
    beats,
    logged,
    async log(body) {
      logged.push(body)
      put(body.case_id, 0, 0)
      return {
        id: `entry-${logged.length}`,
        case_id: body.case_id,
        desk_ticket_id: body.desk_ticket_id ?? null,
        subject_kind: 'tech',
        minutes: body.minutes ?? 0,
        billable: body.billable ?? false,
        source: body.source ?? 'manual',
        executed_at: new Date().toISOString(),
        desk_sync_state: 'synced',
        is_no_time: Boolean(body.no_time),
      }
    },
    async get(caseUuid) {
      return { ...row(caseUuid), prompt_due: false }
    },
    async heartbeat(caseUuid, seconds) {
      beats.push({ caseUuid, seconds })
      const before = row(caseUuid)
      const next = before.seconds + seconds
      return put(caseUuid, next, before.replies, before.seconds < threshold * 60 && next >= threshold * 60)
    },
    async discard(caseUuid) {
      return put(caseUuid, 0, 0)
    },
    async unlogged() {
      return [...rows.values()]
        .filter((r) => r.seconds >= 60)
        .map((r) => ({ ...r, case_number: 'C-00001', case_title: 'Fixture case', desk_ticket_id: '1' }))
    },
  }
}

/** The same words the server uses ("tracked: 7 min across 3 replies"). */
export function trackedSourceText(seconds: number, replies: number): string {
  const amount = seconds > 0 && seconds < 30 ? 'under 1 min' : `${seconds > 0 ? Math.max(1, Math.round(seconds / 60)) : 0} min`
  if (replies <= 0) return `tracked: ${amount}`
  return `tracked: ${amount} across ${replies} repl${replies === 1 ? 'y' : 'ies'}`
}
